import { useEffect, useState } from 'react';
import { Download, X, Share, MoreVertical } from 'lucide-react';
import {
  detectBrowser,
  detectPlatform,
  isMobile,
  isStandalone,
  promptInstall,
  subscribeInstall,
  supportsOneTapInstall,
  type BrowserKind,
  type Platform,
} from '@/utils/installPrompt';

/** 手动引导步骤序号圆点。 */
function Step({ n }: { n: number }) {
  return (
    <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-blue-50 text-xs font-bold text-blue-600">
      {n}
    </span>
  );
}

/**
 * 右上角常驻「保存到桌面」按钮。
 * - 已安装(standalone)时自动隐藏(此时页面已全屏运行)。
 * - 安卓 Chrome 捕获到 beforeinstallprompt → 点击直接唤起系统安装弹窗。
 * - iOS Safari / 其他 → 点击弹出图文安装步骤。
 */
export default function InstallButton() {
  const [available, setAvailable] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [platform, setPlatform] = useState<Platform>('other');
  const [browser, setBrowser] = useState<BrowserKind>('other');

  useEffect(() => {
    const refresh = () => {
      const standalone = isStandalone();
      // 未安装, 且(可一键安装 或 移动端可手动添加)时才显示按钮
      setAvailable(!standalone && (supportsOneTapInstall() || isMobile()));
    };
    refresh();
    const unsub = subscribeInstall(refresh);
    return unsub;
  }, []);

  const handleClick = async () => {
    setPlatform(detectPlatform());
    setBrowser(detectBrowser());
    // 仅在“真能一键安装”的浏览器(安卓/桌面 Chrome/Edge/三星/Opera)直接唤起系统安装框
    if (supportsOneTapInstall()) {
      await promptInstall();
      return; // accepted → appinstalled 事件隐藏按钮；dismissed → 可再点，不弹说明
    }
    // 其余环境(iOS/鸿蒙/夸克/UC/微信等)：弹分环境手动引导
    setShowHelp(true);
  };

  if (!available) return null;

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        className="flex items-center gap-1 rounded-full bg-[rgba(0,229,255,0.12)] px-2.5 py-1 text-xs font-semibold text-accent-cyan transition hover:bg-[rgba(0,229,255,0.2)] active:scale-95"
        aria-label="保存到桌面"
        title="保存到桌面，全屏使用"
      >
        <Download className="h-3.5 w-3.5" />
        <span>保存桌面</span>
      </button>

      {showHelp && (
        <div className="fixed inset-0 z-[70] flex items-end justify-center sm:items-center">
          <div className="absolute inset-0 bg-black/60" onClick={() => setShowHelp(false)} />
          <div className="relative w-full rounded-t-2xl bg-white p-5 pb-[calc(1.25rem+env(safe-area-inset-bottom))] shadow-xl sm:max-w-md sm:rounded-2xl">
            <button
              onClick={() => setShowHelp(false)}
              className="absolute right-3 top-3 rounded-full p-1.5 text-gray-400 active:bg-gray-100"
              aria-label="关闭"
            >
              <X className="h-5 w-5" />
            </button>

            <div className="mb-4 flex items-center gap-3">
              <img src="/icons/icon-192.png" alt="OPEX营策" className="h-12 w-12 rounded-xl shadow-sm" />
              <div>
                <h3 className="text-base font-semibold text-gray-900">保存到桌面</h3>
                <p className="mt-0.5 text-xs text-gray-500">像 App 一样全屏打开，一键直达</p>
              </div>
            </div>

            {browser === 'wechat' ? (
              <div className="space-y-2.5 text-sm text-gray-700">
                <p>微信内置浏览器<b>无法</b>添加到桌面，请先转到系统浏览器：</p>
                <div className="flex items-center gap-2"><Step n={1} />点右上角「⋯」→「在浏览器打开」</div>
                <div className="flex items-center gap-2"><Step n={2} />安卓用 Chrome 打开可一键安装；iOS 用 Safari「分享 → 添加到主屏幕」</div>
              </div>
            ) : platform === 'ios' ? (
              <ol className="space-y-2.5 text-sm text-gray-700">
                <li className="flex items-center gap-2"><Step n={1} />点 Safari 底部 <Share className="h-4 w-4 text-blue-600" /> 分享按钮</li>
                <li className="flex items-center gap-2"><Step n={2} />下滑选择「添加到主屏幕」</li>
                <li className="flex items-center gap-2"><Step n={3} />点右上角「添加」确认</li>
              </ol>
            ) : browser === 'harmony' || browser === 'huawei' || platform === 'harmony' ? (
              <>
                <ol className="space-y-2.5 text-sm text-gray-700">
                  <li className="flex items-center gap-2"><Step n={1} />点浏览器右下 / 右上 <MoreVertical className="h-4 w-4 text-blue-600" /> 菜单</li>
                  <li className="flex items-center gap-2"><Step n={2} />选择「添加到桌面」或「添加书签 / 快捷方式」</li>
                  <li className="flex items-center gap-2"><Step n={3} />确认添加</li>
                </ol>
                <p className="mt-3 rounded-lg bg-amber-50 p-2.5 text-xs leading-relaxed text-amber-700">
                  鸿蒙 / 华为浏览器暂不支持网页应用一键安装，桌面图标为快捷方式（打开可能仍带地址栏）。如需点一下就装的原生 App 图标，请用 Chrome 打开，或等待我们发布原生 App。
                </p>
              </>
            ) : browser === 'quark' ? (
              <>
                <ol className="space-y-2.5 text-sm text-gray-700">
                  <li className="flex items-center gap-2"><Step n={1} />点夸克右下角「三横」菜单（或首页「工具箱」）</li>
                  <li className="flex items-center gap-2"><Step n={2} />选择「添加到桌面 / 加入书签」</li>
                  <li className="flex items-center gap-2"><Step n={3} />确认添加</li>
                </ol>
                <p className="mt-3 rounded-lg bg-amber-50 p-2.5 text-xs leading-relaxed text-amber-700">
                  夸克浏览器不支持网页一键安装，只能手动添加为桌面快捷方式。想要点一下就装的原生体验，建议改用系统 Chrome 打开本站。
                </p>
              </>
            ) : browser === 'uc' ? (
              <ol className="space-y-2.5 text-sm text-gray-700">
                <li className="flex items-center gap-2"><Step n={1} />点 UC 底部「菜单」</li>
                <li className="flex items-center gap-2"><Step n={2} />选择「添加到桌面」</li>
                <li className="flex items-center gap-2"><Step n={3} />确认添加</li>
              </ol>
            ) : platform === 'android' ? (
              <ol className="space-y-2.5 text-sm text-gray-700">
                <li className="flex items-center gap-2"><Step n={1} />点浏览器右上 <MoreVertical className="h-4 w-4 text-blue-600" /> 菜单</li>
                <li className="flex items-center gap-2"><Step n={2} />选择「添加到主屏幕」或「安装应用」</li>
                <li className="flex items-center gap-2"><Step n={3} />确认添加即可</li>
              </ol>
            ) : (
              <p className="text-sm text-gray-700">
                点浏览器地址栏右侧的安装图标，或在菜单中选择「安装应用 / 创建快捷方式」，即可保存到桌面。
              </p>
            )}

            <div className="mt-5">
              <button
                onClick={() => setShowHelp(false)}
                className="w-full rounded-xl bg-gray-100 py-3 font-medium text-gray-600 active:bg-gray-200"
              >
                知道了
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
